// Every number a designer would want to turn. Nothing here imports from the
// rest of the sim, so the client, the server and the tests all agree on one
// set of values.
//
// The list is short on purpose. This game has one attack, one enemy, one
// consumable and one room; a constant that only one call site reads and that
// nobody would ever tune belongs at that call site, not in here.

/** Server simulation rate. */
export const TICK_RATE = 20;
export const TICK_DT = 1 / TICK_RATE;

/** Logical pixels per movement/collision tile. */
export const TILE = 16;

/** The world is two rectangles stamped into one tile grid: the field on top,
 *  the shop interior parked underneath it behind solid rock. They are kept
 *  apart by `zoneAt`, never by distance — see src/sim/map.ts. */
export const FIELD_W = 40;
export const FIELD_H = 26;
export const SHOP_W = 15;
export const SHOP_H = 10;
/** First tile row of the shop interior. The row above it is solid. */
export const SHOP_TOP = FIELD_H + 1;

export const WORLD_TILES_W = FIELD_W;
export const WORLD_TILES_H = SHOP_TOP + SHOP_H;
export const WORLD_W = WORLD_TILES_W * TILE;
export const WORLD_H = WORLD_TILES_H * TILE;

// The virtual framebuffer is deliberately NOT here: it is mutable, it belongs
// to a browser window, and this file is imported by the Worker. See
// src/client/render/view.ts.

/** Concurrency cap. Past this the world is full and says so. */
export const MAX_PLAYERS = 100;

/** Most player bodies one client is sent, including itself. Oozes are never
 *  displaced by a crowd: they affect play, while remote players do not collide
 *  with or damage one another. Keeping this bound on the server also bounds
 *  JSON encoding, socket traffic and canvas work when the field is full. */
export const MAX_VISIBLE_PLAYERS = 32;

/** Entities stream in at AOI_ENTER and stop at AOI_LEAVE, so something sitting
 *  exactly on the boundary cannot flicker.
 *
 *  **AOI_ENTER is a floor, not a budget.** It has to clear the half-diagonal of
 *  the *widest* framebuffer the client will ever adopt — 253 pixels — because
 *  anything nearer than that can be on screen, and a body that is on screen and
 *  not in the snapshot is a body that pops into existence in front of you. The
 *  margin on top of 253 is the sprite: a figure is drawn from its feet and
 *  stands thirty pixels tall, so its head is visible before its centre is.
 *
 *  This radius therefore covers most of a field that is only 640x416, and that
 *  is not an oversight to be tuned away — it is what happens when the map is
 *  barely two screens across. The lever for snapshot cost is what each body
 *  weighs and how many are sent (MAX_VISIBLE_PLAYERS), never this number.
 *  `snapshot.test.ts` holds the floor. */
export const AOI_ENTER = 280;
export const AOI_LEAVE = 350;

/** Every walking body occupies exactly one cell against terrain. */
export const GRID_HALF = TILE / 2;
/** Hit boxes are smaller than the cell, so a body has to be genuinely on top
 *  of you rather than merely in the next cell over. */
export const BODY_HALF = 6;

// --- the player -------------------------------------------------------------

export const PLAYER_SPEED = 72;
export const PLAYER_MAX_HP = 20;

/** The sword. One attack, one damage number, one cooldown. */
export const SWORD_DAMAGE = 4;
export const SWORD_COOLDOWN = 0.55;
/** How long the swing is drawn and how long it can land. */
export const SWORD_SWING = 0.18;
/** Centre of the swing arc, measured forward from the body. */
export const SWORD_REACH = 14;
export const SWORD_HALF = 10;

/** Standing still mends you, very slowly. You have to be genuinely idle for
 *  REGEN_DELAY before the first point, so it is never a combat heal. */
export const REGEN_DELAY = 3;
export const REGEN_INTERVAL = 4;

/** Being hit hurts once per this window, whatever is touching you. */
export const HIT_INVULN = 0.8;

/** Death costs the walk back and nothing else. */
export const RESPAWN_SECONDS = 3;

// --- the enemy --------------------------------------------------------------

export const ENEMY_MAX_HP = 12;
export const ENEMY_SPEED = 34;
export const ENEMY_TOUCH_DAMAGE = 2;
/** How often one ooze can land its touch on the same player. */
export const ENEMY_TOUCH_COOLDOWN = 1.1;
/** It notices you inside this, and forgets you outside the leash. */
export const ENEMY_SIGHT = 90;
export const ENEMY_LEASH = 190;
/** How many oozes the field holds, and how fast a dead one is replaced. */
export const ENEMY_COUNT = 14;
export const ENEMY_RESPAWN_SECONDS = 8;

/** Gold for a kill. The ooze drops nothing on the floor — the reward is paid
 *  straight into the purse — but it is a loot payout in every other respect:
 *  the killer is the one who gets it, and it is the only income in the game. */
export const ENEMY_GOLD = 3;

// --- the shop ---------------------------------------------------------------

export const POTION_COST = 10;
export const POTION_HEAL = 10;
/** How close to the shopkeeper you have to stand to trade. */
export const SHOP_RANGE = 26;

// --- chat and sockets -------------------------------------------------------

export const CHAT_MAX_LENGTH = 160;
export const CHAT_HISTORY_LIMIT = 60;
export const CHAT_MIN_INTERVAL_SECONDS = 0.75;

/** Largest client frame accepted, and how many per tick. */
export const MAX_FRAME_BYTES = 2048;
export const MAX_MSGS_PER_TICK = 12;

/** A dropped socket leaves the body standing this long, so a blip does not
 *  cost your position. */
export const LINKDEAD_SECONDS = 20;
